/**
 * Celebration — the Reading Room's finished-scorecard gesture.
 *
 * Applies a gentle brightness pulse to the anchored card's atmosphere layer
 * via a `data-celebration-lamp` attribute, then briefly fans a dozen small
 * paper score slips behind the card. Used by the Affirmation on mount.
 */
import { useEffect, type RefObject } from 'react';

import './Celebration.css';

export interface CelebrationProps {
  /** Flip to true to fire the gesture. Reset by `onDone`. */
  active: boolean;
  /** Called after the gesture completes so the parent can re-arm. */
  onDone?: () => void;
  /** The card whose atmosphere layer we brighten. */
  anchorRef?: RefObject<HTMLElement | null>;
}

const LAMP_BRIGHTEN_DURATION_MS = 1400;

const PAPER_FLECKS = Array.from({ length: 12 }, (_, index) => index);

export function Celebration({
  active,
  onDone,
  anchorRef,
}: CelebrationProps): React.JSX.Element | null {
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => onDone?.(), LAMP_BRIGHTEN_DURATION_MS + 80);
    return () => clearTimeout(t);
  }, [active, onDone]);

  useEffect(() => {
    if (!active) return;
    const el = anchorRef?.current;
    if (!el) return;
    el.dataset['celebrationLamp'] = 'on';
    const t = window.setTimeout(() => {
      delete el.dataset['celebrationLamp'];
    }, LAMP_BRIGHTEN_DURATION_MS + 40);
    return () => {
      window.clearTimeout(t);
      delete el.dataset['celebrationLamp'];
    };
  }, [active, anchorRef]);

  return active ? (
    <span className="celebration celebration--lamp" aria-hidden="true">
      <span className="celebration__halo" />
      {PAPER_FLECKS.map((index) => (
        <span className="celebration__fleck" key={index} />
      ))}
    </span>
  ) : null;
}
